import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import Button from '@/components/ui/Button'
import Field from '@/components/ui/Field'
import Modal from '@/components/ui/Modal'
import { nameTaken, type BoardSummary } from '@/features/mitra/api/dashboard'

/**
 * NO TWO DASHBOARDS SHARE A NAME (owner, 2026-10-20). A name the reader
 * already uses is said under the field as they type it, from the list on
 * screen, and the engine's own refusal (`name_taken`) lands in the same
 * place -- each with a way to open the dashboard that has the name, which is
 * nearly always what a reader typing it wanted.
 */

/** The dashboard a clash names, as one line and a button to open it. */
export function OpenExisting({
  id,
  label = 'Open the existing one',
}: {
  id: string
  label?: string
}) {
  const navigate = useNavigate()
  return (
    <Button
      size="sm"
      variant="secondary"
      data-role="open-existing"
      onClick={() => {
        void navigate({ to: '/dashboards/$dashboardId', params: { dashboardId: id } })
      }}
    >
      {label}
    </Button>
  )
}

/** What a name field says: the local clash first, then the engine's refusal. */
export function useNameCheck(boards: readonly BoardSummary[], name: string, but?: string) {
  const clash = nameTaken(boards, name, but)
  return {
    clash,
    message: clash ? `You already have a dashboard called “${clash.name}”.` : undefined,
  }
}

export interface NameRefusal {
  message: string
  existing: string | null
}

/**
 * A name asked for before a dashboard is made: an empty one, or a copy of a
 * template the reader already has. Saving hands the name up; the caller
 * makes the dashboard and hands back the engine's refusal, if any.
 */
export default function NameDialog({
  open,
  title,
  initial,
  boards,
  busy,
  refusal,
  onSave,
  onClose,
}: {
  open: boolean
  title: string
  initial: string
  boards: readonly BoardSummary[]
  busy: boolean
  refusal: NameRefusal | null
  onSave: (name: string) => void
  onClose: () => void
}) {
  const [name, setName] = useState(initial)
  const { clash, message } = useNameCheck(boards, name)
  const ready = name.trim() !== '' && !clash && !busy
  const existing = clash?.id ?? refusal?.existing ?? null
  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title={title}
      size="sm"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!ready}
            onClick={() => {
              onSave(name.trim())
            }}
          >
            {busy ? 'Making it…' : 'Make it'}
          </Button>
        </div>
      }
    >
      <form
        data-role="name-dialog"
        onSubmit={(event) => {
          event.preventDefault()
          if (ready) onSave(name.trim())
        }}
        className="flex flex-col gap-3"
      >
        <Field
          label="Name"
          value={name}
          maxLength={60}
          autoFocus
          error={message ?? refusal?.message}
          onChange={(e) => {
            setName(e.target.value)
          }}
        />
        {existing ? (
          <div>
            <OpenExisting id={existing} />
          </div>
        ) : null}
      </form>
    </Modal>
  )
}

/** A name not yet used: `base`, else `base 2`, `base 3`… as a suggestion the reader can change. */
export function freeName(boards: readonly BoardSummary[], base: string): string {
  if (!nameTaken(boards, base)) return base
  for (let n = 2; n < 100; n += 1) {
    const next = `${base} ${String(n)}`
    if (!nameTaken(boards, next)) return next
  }
  return base
}
